import styled from '@emotion/styled';
import { Modal, type ModalProps, Spin } from 'antd';
import type React from 'react';
import { type FC, useEffect, useState } from 'react';
import { useIsPhone } from 'src/lib/customHooks';
import { trackEvent } from 'src/lib/googleAnalytics';
import { appSupports, postToApp } from 'src/lib/player/appBridge';
import { isInApp } from 'src/lib/player/appLocationPermission';
import { t, tt } from 'src/lib/player/i18n';
import { buildShareUrl } from 'src/lib/player/shareUrl';
import { type StarterType, useStore } from 'src/lib/player/store';
import { correctedNow } from 'src/lib/player/utils/clock';
import { RACE_VIDEO_GRACE_MS, raceVideoFileName } from 'src/lib/raceVideo';
import { IconDownload, IconSharePlatformAware, IconVideo } from '../../BasicComponents/Icons';
import { type RaceVideoState, useRaceVideo } from './useRaceVideo';

/** The clip as a file, which is what Instagram and other apps accept from the share sheet. */
async function fetchFile(url: string, name: string): Promise<File | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return new File([await res.blob()], name, { type: 'video/mp4' });
  } catch {
    // Without the file, Share falls back to the link and Download opens the video.
    return null;
  }
}

/** Phone share sheets offer saving the file, so a Download button beside them only duplicates it. */
function canShareVideoFiles(): boolean {
  const probe = new File([], 'probe.mp4', { type: 'video/mp4' });
  return navigator.canShare?.({ files: [probe] }) ?? false;
}

/** The player with this starter selected, so whoever receives the clip finds the race. */
function playerUrl(starterId: string): string {
  const { origin, pathname } = window.location;
  return buildShareUrl(
    { origin, pathname, selected: [starterId], time: 0, viewport: null },
    { favorites: true, time: false, viewport: false },
  );
}

/** The file is fetched before the click: an await ahead of `navigator.share` uses up Safari's user activation. */
async function share(url: string, file: File | null, title: string, text: string) {
  try {
    if (file != null && navigator.canShare?.({ files: [file] })) {
      trackEvent('race_video_share', { method: 'file' });
      await navigator.share({ files: [file], title, text });
    } else {
      trackEvent('race_video_share', { method: 'link' });
      await navigator.share({ url, title, text });
    }
  } catch (err) {
    // Dismissing the sheet rejects with AbortError. That is not a failure, so stay quiet.
    if (!(err instanceof Error && err.name === 'AbortError')) {
      console.warn('Native share failed:', err);
    }
  }
}

function download(url: string, file: File | null) {
  trackEvent('race_video_download');
  if (file == null) {
    // A cross-origin link ignores `download`, so the browser opens the video instead.
    window.open(url, '_blank', 'noopener');
    return;
  }
  const objectUrl = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = file.name;
  link.click();
  // Some engines read the blob URL after click() returns.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

type ClipActions = { onShare?: () => void; onDownload?: () => void };

/** Which buttons the clip gets here; a missing handler hides its button. */
function videoActions(clip: {
  url: string;
  file: File | null;
  fileFailed: boolean;
  name: string;
  title: string;
  text: string;
  fullScreen: boolean;
}): ClipActions {
  const { url, file, name } = clip;
  // The app's WebView has no share sheet and drops blob downloads, so the app does both.
  if (appSupports('race-video')) {
    const ask = (action: 'share' | 'download') => () =>
      postToApp({ type: 'racemap-race-video', action, url, fileName: name });
    return { onShare: ask('share'), onDownload: ask('download') };
  }
  const hasShare = typeof navigator.share === 'function';
  // Without the file the sheet only gets a link, so Download is the one way left to save the clip.
  // An older app build drops the download, so a button there would do nothing.
  const hasDownload =
    !isInApp() && (clip.fileFailed || !(hasShare && clip.fullScreen && canShareVideoFiles()));
  return {
    onShare: hasShare ? () => share(url, file, clip.title, clip.text) : undefined,
    onDownload: hasDownload ? () => download(url, file) : undefined,
  };
}

function message(state: RaceVideoState): string | null {
  switch (state.status) {
    case 'loading':
    case 'rendering':
      return t('raceVideo.rendering');
    case 'unavailable':
      return state.reason === 'dnf' ? t('raceVideo.unavailable.dnf') : t('raceVideo.unavailable');
    case 'notFinished':
      return t('raceVideo.notFinished');
    case 'error':
      return t('raceVideo.error');
    default:
      return null;
  }
}

const RaceVideoView: FC<{
  eventId: string;
  starter: StarterType;
  title: string;
  fullScreen: boolean;
}> = ({ eventId, starter, title, fullScreen }) => {
  const state = useRaceVideo(eventId, starter.id);
  const [file, setFile] = useState<File | null>(null);
  const [fileFailed, setFileFailed] = useState(false);
  const url = state.status === 'ready' ? state.url : null;
  const name = raceVideoFileName(starter);

  useEffect(() => {
    // The app downloads the clip itself, so the WebView need not fetch it too.
    if (url == null || appSupports('race-video')) return;
    let cancelled = false;
    void fetchFile(url, name).then((fetched) => {
      if (cancelled) return;
      setFile(fetched);
      setFileFailed(fetched == null);
    });
    return () => {
      cancelled = true;
    };
  }, [url, name]);

  if (state.status !== 'ready') {
    const waiting = state.status === 'loading' || state.status === 'rendering';
    return (
      <Hint data-testid="race-video-status" fullScreen={fullScreen}>
        {waiting && <Spin size="large" data-testid="race-video-spinner" />}
        <div>{message(state)}</div>
      </Hint>
    );
  }

  const { onShare, onDownload } = videoActions({
    url: state.url,
    file,
    fileFailed,
    name,
    title,
    text: tt('raceVideo.shareText', playerUrl(starter.id)),
    fullScreen,
  });
  const DownloadButton = onShare ? ActionButton : PrimaryButton;
  return (
    <Wrapper fullScreen={fullScreen}>
      <Video
        fullScreen={fullScreen}
        src={state.url}
        controls
        playsInline
        autoPlay
        muted
        loop
        onLoadedData={() => trackEvent('race_video_ready')}
      />
      <Actions>
        {onShare && (
          <PrimaryButton type="button" data-testid="race-video-share" onClick={onShare}>
            <IconSharePlatformAware />
            <span>{t('raceVideo.share')}</span>
          </PrimaryButton>
        )}
        {onDownload && (
          <DownloadButton type="button" data-testid="race-video-download" onClick={onDownload}>
            <IconDownload />
            <span>{t('raceVideo.download')}</span>
          </DownloadButton>
        )}
      </Actions>
    </Wrapper>
  );
};

/* Phones get the whole screen, as the story viewers people share from do. */
const FULL_SCREEN_STYLE: React.CSSProperties = {
  top: 0,
  maxWidth: '100vw',
  margin: 0,
  paddingBottom: 0,
};
const FULL_SCREEN_STYLES: ModalProps['styles'] = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    padding: '12px 16px calc(16px + env(safe-area-inset-bottom))',
    borderRadius: 0,
    background: 'black',
  },
  header: { marginBottom: 12, background: 'transparent' },
  title: { color: 'white' },
  close: { color: 'white' },
  body: { display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 },
};
/* A lightbox on desktop: the dimmed player stays as context, the clip is the focus. */
const LIGHTBOX_STYLES: ModalProps['styles'] = { mask: { background: 'rgb(0 0 0 / 80%)' } };

/** Opens a finished starter's race video; `icon` for the sidebar row, `button` for the stats tab. */
export const RaceVideoButton: FC<{ starter: StarterType; variant?: 'button' | 'icon' }> = ({
  starter,
  variant = 'button',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isPhone = useIsPhone();
  const event = useStore((state) => state.events[starter.eventIndex]);

  if (event == null || !event.options.hasRaceVideo) return null;
  if (correctedNow() < event.endTime + RACE_VIDEO_GRACE_MS) return null;

  const title = tt('raceVideo.title', starter.name ?? starter.startNumber ?? '');
  function open() {
    trackEvent('race_video_open');
    setIsOpen(true);
  }
  // Clicks inside the modal bubble through the portal too; the row behind must not see them.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only keeps clicks from the row behind
    <span onClick={stop} onKeyUp={stop}>
      {variant === 'icon' ? (
        <IconButton
          type="button"
          title={t('raceVideo.button')}
          aria-label={t('raceVideo.button')}
          data-testid="race-video-button"
          onClick={open}
        >
          <IconVideo />
        </IconButton>
      ) : (
        <button
          type="button"
          className="button"
          title={t('raceVideo.button')}
          data-testid="race-video-button"
          onClick={open}
        >
          <IconVideo />
        </button>
      )}
      <Modal
        open={isOpen}
        destroyOnHidden
        onCancel={() => setIsOpen(false)}
        title={title}
        footer={null}
        centered={!isPhone}
        width={isPhone ? '100vw' : 420}
        style={isPhone ? FULL_SCREEN_STYLE : undefined}
        styles={isPhone ? FULL_SCREEN_STYLES : LIGHTBOX_STYLES}
      >
        {isOpen && (
          <RaceVideoView
            eventId={event.eventId}
            starter={starter}
            title={title}
            fullScreen={isPhone}
          />
        )}
      </Modal>
    </span>
  );
};

/* Bare, so the row's control styles size and colour the icon like the star and eye beside it. */
const IconButton = styled.button`
  display: flex;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
`;

const Wrapper = styled.div<{ fullScreen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${({ fullScreen }) => fullScreen && 'flex: 1; min-height: 0;'}
`;

const Video = styled.video<{ fullScreen: boolean }>`
  width: 100%;
  max-height: 70vh;
  aspect-ratio: 9 / 16;
  background: black;
  border-radius: 4px;
  ${({ fullScreen }) =>
    fullScreen &&
    'flex: 1; min-height: 0; max-height: none; aspect-ratio: auto; object-fit: contain;'}
`;

const Hint = styled.div<{ fullScreen: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px 0;
  text-align: center;
  color: ${({ fullScreen }) => (fullScreen ? 'white' : 'var(--text-color)')};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

/* Real buttons, so Safari gets an activation source it can anchor the share sheet to. */
const ActionButton = styled.button`
  display: flex;
  flex: 1;
  gap: 8px;
  justify-content: center;
  align-items: center;
  min-height: 44px;
  padding: 8px 10px;
  border: 1px solid var(--base-color-50);
  border-radius: 4px;
  background: white;
  color: var(--text-color);
  font: inherit;
  cursor: pointer;
  font-weight: 600;
  user-select: none;

  &:active {
    opacity: 0.6;
  }
`;

const PrimaryButton = styled(ActionButton)`
  border-color: var(--base-color);
  background: var(--base-color);
  color: white;
`;
